import { useId, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import styles from './LocationServiceCard.module.css';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getLocationService, openFromLocation } from '@/lib/serviceRequests';
import jobs from '@/pages/partner-jobs/PartnerJobs.module.css';

/**
 * Service on a location (Warren, ruling H, 21 October 2026): each charger's installer of record, and any open service call for
 * EVI staff (can_see_calls: a site team hears of a call when EVI contacts them, Warren, 4 Nov 2026). EVI and the site's own
 * admins see the card (the Api answers 404 to anyone else, and this card renders nothing). Opening a call is EVI ops's
 * (can_open), and a new call always opens with EVI ops (ruling R4).
 */
export function LocationServiceCard({ locationId }: { locationId: string }) {
  const [expanded, setExpanded] = useState(false);
  const contentId = useId();
  const client = useQueryClient(), key = ['location-service', locationId];
  const q = useQuery({ queryKey: key, queryFn: () => getLocationService(locationId), retry: false });
  const [charger, setCharger] = useState(''), [summary, setSummary] = useState('');
  const [busy, setBusy] = useState(false), [message, setMessage] = useState('');
  const v = q.data;
  if (!v || !v.chargers.length) return null;
  async function open() {
    setBusy(true); setMessage('');
    try {
      const r = await openFromLocation(locationId, { charger_id: charger, summary });
      setMessage(r.created ? 'Service call opened.' : 'Added to the charger\'s open service call.'); setSummary('');
      await client.invalidateQueries({ queryKey: key });
    } catch (err) { setMessage(err instanceof Error ? err.message : 'The service call was not opened.'); } finally { setBusy(false); }
  }
  return <section className={jobs.card} aria-label="Service">
    <h2 className={styles.heading}>
      <button type="button" className={styles.toggle} aria-expanded={expanded} aria-controls={contentId} onClick={() => setExpanded(value => !value)}>
        <span>Service</span><ChevronDown size={16} aria-hidden="true" className={expanded ? styles.expanded : undefined} />
      </button>
    </h2>
    <div id={contentId} className={styles.body} hidden={!expanded}>
    <p className={jobs.hint}>EV Initiative answers a service call first, and contacts the installer of record when it needs them. EVI never prices or bills service work.</p>
    <ul className={jobs.list}>{v.chargers.map(c => <li key={c.id}>
      <span><strong>{c.identity}</strong> <span className={jobs.sub}>{c.installer ? `Installed by ${c.installer}` : 'No installer of record'}</span></span>
      {c.open_request ? (c.open_request.id ? <a href={`/service-requests/${c.open_request.id}`}>{c.open_request.status_label}</a> : <span className={jobs.slotTag}>{c.open_request.status_label}</span>)
        : v.can_see_calls && <span className={jobs.slotTag} data-tone="good">No open call</span>}
    </li>)}</ul>
    {v.can_open && <details><summary>Open a service call</summary>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 12 }}>
        <label className={jobs.field}><span>Charger</span><select value={charger} onChange={e => setCharger(e.target.value)}>
          <option value="">Choose a charger</option>{v.chargers.map(c => <option key={c.id} value={c.id}>{c.identity}</option>)}</select></label>
        <label className={jobs.field}><span>What is wrong (no names or phone numbers)</span><input value={summary} maxLength={1000} onChange={e => setSummary(e.target.value)} /></label>
        <div className={jobs.buttons}><button type="button" className={jobs.ghost} disabled={busy || !charger || summary.trim().length < 10} onClick={() => void open()}>Open the call</button></div>
      </div>
    </details>}
    <p className={jobs.message} role="status">{message}</p>
    </div>
  </section>;
}
